import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { getErrors, formatForCopy } from '../../utils/bugReport';
import './BugReportRecorder.css';

const DRAFT_KEY = 'bugReportDraft.v1';
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const blobToDataUrl = (blob) => new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => resolve(null);
    r.readAsDataURL(blob);
});

const dataUrlToBlob = async (dataUrl) => {
    try { return await (await fetch(dataUrl)).blob(); } catch { return null; }
};

/**
 * Report a bug by talking. Record → review → (optionally) add a note and/or a
 * screenshot → Send, or Save draft to finish later (kept in localStorage, like
 * the meeting-minutes recorder). Console errors captured this session ride along
 * as text, invisibly. If the mic is blocked the note box still works — a
 * text-only report is fine. A screenshot can be picked from a file or pasted
 * straight from the clipboard.
 */
const BugReportRecorder = ({ onClose }) => {
    const [phase, setPhase] = useState('idle');      // idle | recording | recorded
    const [seconds, setSeconds] = useState(0);
    const [audioUrl, setAudioUrl] = useState(null);
    const [note, setNote] = useState('');
    const [micError, setMicError] = useState('');
    const [imageUrl, setImageUrl] = useState(null);
    const [imageError, setImageError] = useState('');
    const [status, setStatus] = useState('');        // '', 'sending', 'sent', 'error'
    const [hasDraft, setHasDraft] = useState(false);

    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const blobRef = useRef(null);
    const imageBlobRef = useRef(null);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);

    // Offer to restore a saved draft on open.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) setHasDraft(true);
        } catch (_) { /* ignore */ }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            if (imageUrl) URL.revokeObjectURL(imageUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopTracks = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const startRecording = useCallback(async () => {
        setMicError('');
        // getUserMedia only exists in a secure context. Opening the app over a
        // plain-HTTP LAN address (e.g. http://192.168.x.x:3000) leaves
        // navigator.mediaDevices undefined, so the call throws before the
        // browser ever shows a permission prompt. localhost/127.0.0.1 are the
        // only HTTP origins the browser treats as secure.
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
            setMicError(
                'Voice recording needs a secure (https://) connection. This page is served over http, '
                + 'so the browser blocks the microphone and shows no prompt. You can still type a note below.'
            );
            return;
        }
        try {
            // Ask for clean speech: mono, with the browser's voice DSP on. The
            // default (`audio: true`) leaves the bitrate to the UA, which on some
            // builds is ~24 kbps and sounds heavily compressed — pin it to a
            // comfortable speech bitrate instead.
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            streamRef.current = stream;
            const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
                .find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
            const rec = new MediaRecorder(stream, {
                ...(mime ? { mimeType: mime } : {}),
                audioBitsPerSecond: 128000
            });
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
            rec.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || mime || 'audio/webm' });
                blobRef.current = blob;
                if (audioUrl) URL.revokeObjectURL(audioUrl);
                setAudioUrl(URL.createObjectURL(blob));
                setPhase('recorded');
                stopTracks();
            };
            rec.start();
            recorderRef.current = rec;
            startedAtRef.current = Date.now();
            setSeconds(0);
            setPhase('recording');
            timerRef.current = setInterval(() => {
                setSeconds(Math.round((Date.now() - startedAtRef.current) / 1000));
            }, 250);
        } catch (err) {
            console.error('[bug-recorder] mic error:', err);
            setMicError('Microphone unavailable or permission denied. You can still type a note below.');
        }
    }, [audioUrl]);

    const stopRecording = useCallback(() => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop();
        }
    }, []);

    const reset = useCallback(() => {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        blobRef.current = null;
        setAudioUrl(null);
        setSeconds(0);
        setPhase('idle');
    }, [audioUrl]);

    const acceptImage = useCallback((file) => {
        setImageError('');
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setImageError('That is not an image file.');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setImageError('Screenshot is larger than 10 MB.');
            return;
        }
        imageBlobRef.current = file;
        setImageUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
    }, []);

    const onPickImage = useCallback((e) => {
        acceptImage(e.target.files?.[0] || null);
        e.target.value = '';
    }, [acceptImage]);

    const onPaste = useCallback((e) => {
        const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
        if (item) {
            e.preventDefault();
            acceptImage(item.getAsFile());
        }
    }, [acceptImage]);

    const clearImage = useCallback(() => {
        imageBlobRef.current = null;
        setImageError('');
        setImageUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    }, []);

    const saveDraft = useCallback(async () => {
        const draft = { note, seconds, createdAt: Date.now(), audio: null, image: null };
        if (blobRef.current) draft.audio = await blobToDataUrl(blobRef.current);
        if (imageBlobRef.current) draft.image = await blobToDataUrl(imageBlobRef.current);
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
            setStatus('');
            setHasDraft(true);
            onClose();
        } catch (err) {
            console.error('[bug-recorder] draft save failed (too large?):', err);
            setStatus('error');
        }
    }, [note, seconds, onClose]);

    const restoreDraft = useCallback(async () => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (!raw) return;
            const draft = JSON.parse(raw);
            setNote(draft.note || '');
            setSeconds(draft.seconds || 0);
            if (draft.audio) {
                const blob = await dataUrlToBlob(draft.audio);
                if (blob) {
                    blobRef.current = blob;
                    setAudioUrl(URL.createObjectURL(blob));
                    setPhase('recorded');
                }
            }
            if (draft.image) {
                const blob = await dataUrlToBlob(draft.image);
                if (blob) {
                    imageBlobRef.current = blob;
                    setImageUrl(URL.createObjectURL(blob));
                }
            }
            setHasDraft(false);
        } catch (err) {
            console.error('[bug-recorder] draft restore failed:', err);
        }
    }, []);

    const discardDraft = useCallback(() => {
        try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* ignore */ }
        setHasDraft(false);
    }, []);

    const isEmpty = () => !blobRef.current && !imageBlobRef.current && !note.trim();

    const send = useCallback(async () => {
        if (isEmpty()) return;
        setStatus('sending');
        try {
            const fd = new FormData();
            if (blobRef.current) {
                const ext = (blobRef.current.type.split('/')[1] || 'webm').split(';')[0];
                fd.append('audio', blobRef.current, `bug-report.${ext}`);
                if (seconds) fd.append('durationSeconds', String(seconds));
            }
            if (imageBlobRef.current) {
                const ext = (imageBlobRef.current.type.split('/')[1] || 'png').split(';')[0];
                fd.append('image', imageBlobRef.current, `bug-screenshot.${ext}`);
            }
            if (note.trim()) fd.append('note', note.trim());
            if (getErrors().length) fd.append('report', formatForCopy());
            fd.append('url', window.location.href);

            await axiosInstance.post('/api/bug-reports', fd);
            discardDraft();
            setStatus('sent');
            setTimeout(onClose, 1200);
        } catch (err) {
            console.error('[bug-recorder] send failed:', err);
            setStatus('error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note, seconds, discardDraft, onClose]);

    const mmss = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

    return (
        <div className="bugrec-overlay" onClick={onClose}>
            <div className="bugrec" onClick={(e) => e.stopPropagation()} onPaste={onPaste}>
                <div className="bugrec__head">
                    <span>🐞 Report a bug</span>
                    <button type="button" className="bugrec__x" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {status === 'sent' ? (
                    <div className="bugrec__done">✓ Sent. Thank you!</div>
                ) : (
                    <>
                        {hasDraft && phase === 'idle' && (
                            <div className="bugrec__draft">
                                You have a saved draft.
                                <button type="button" onClick={restoreDraft}>Restore</button>
                                <button type="button" className="bugrec__link" onClick={discardDraft}>Discard</button>
                            </div>
                        )}

                        <p className="bugrec__hint">Tell us what went wrong — just talk. Add a note or a screenshot if it helps.</p>

                        <div className="bugrec__recorder">
                            {phase === 'idle' && (
                                <button type="button" className="bugrec__mic" onClick={startRecording}>
                                    ● Start recording
                                </button>
                            )}
                            {phase === 'recording' && (
                                <button type="button" className="bugrec__mic bugrec__mic--rec" onClick={stopRecording}>
                                    ■ Stop — {mmss}
                                </button>
                            )}
                            {phase === 'recorded' && (
                                <div className="bugrec__playback">
                                    <audio controls src={audioUrl} />
                                    <button type="button" className="bugrec__link" onClick={reset}>Re-record</button>
                                </div>
                            )}
                        </div>

                        {micError && <div className="bugrec__err">{micError}</div>}

                        <textarea
                            className="bugrec__note"
                            rows={3}
                            maxLength={5000}
                            placeholder="Optional note (what you were doing, what you expected)…"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />

                        <div className="bugrec__shot">
                            {imageUrl ? (
                                <div className="bugrec__shot-preview">
                                    <img src={imageUrl} alt="Screenshot to attach" />
                                    <button type="button" className="bugrec__link" onClick={clearImage}>Remove screenshot</button>
                                </div>
                            ) : (
                                <label className="bugrec__shot-add">
                                    📷 Add screenshot
                                    <input type="file" accept="image/*" onChange={onPickImage} hidden />
                                    <span className="bugrec__shot-hint"> or paste with Ctrl+V</span>
                                </label>
                            )}
                        </div>

                        {imageError && <div className="bugrec__err">{imageError}</div>}

                        {status === 'error' && (
                            <div className="bugrec__err">Could not send. Try again, or Save draft.</div>
                        )}

                        <div className="bugrec__actions">
                            <button
                                type="button"
                                className="bugrec__secondary"
                                onClick={saveDraft}
                                disabled={status === 'sending' || isEmpty()}
                            >
                                Save draft
                            </button>
                            <button
                                type="button"
                                className="bugrec__primary"
                                onClick={send}
                                disabled={status === 'sending' || phase === 'recording' || isEmpty()}
                            >
                                {status === 'sending' ? 'Sending…' : 'Send report'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default BugReportRecorder;
